/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  ConfigQueryResults,
  Configuration,
  CoreService,
  FlowRunLogs,
  ListResultsQuery,
  NetBenefitAnalysisV2Columns,
  NetBenefitAnalysisV2Request,
  PlantSimDatabaseResultsEntry,
  RequestWithName,
  ScenarioRecord,
  ScenarioWorkflow,
  Task,
} from '@poweralpha/common';
import type { PlantSimRequest } from '@poweralpha/common/models/plantsim';
import {
  keepPreviousData,
  type QueryFunctionContext,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  api,
  getQueryConfigsSearchParams,
  getServiceSearchParams,
  type QueryConfigsSearchParams,
  type ServiceListResultsSearchParams,
} from '../api';
import { getPriceLookups, type PriceLookups } from '../database/prices/api';
import { priceTypesForApi } from '../plant/data';
import type { ConfigType } from '../react-components/configs/models';

type PlantSimV2Configuration = Configuration<PlantSimRequest>;
type PlantSimV2RequestWithName = RequestWithName<PlantSimRequest>;

interface PlantSimSingleRunParameters {
  user?: string;
  config_id?: [string, number];
  request?: PlantSimRequest;
}

export type PlantSimNestedResult = Record<string, unknown>;

export interface PlantSimInputsOutputsResponse {
  selected_year: number | 'all';
  available_years: Array<number>;
  sum: PlantSimNestedResult;
  average: PlantSimNestedResult;
}

export interface PlantSimTimeseriesResponse {
  year: number;
  month: number | null;
  granularity: string;
  timestamps: Array<string>;
  series: PlantSimNestedResult;
  units: PlantSimNestedResult;
}

export interface PlantSimMonthlyResponse {
  year: number;
  months: Array<{
    month: number;
    timestamp: string;
    sum: PlantSimNestedResult;
    average: PlantSimNestedResult;
  }>;
}

export interface PlantSimTwelveByTwentyFourResponse {
  year: number;
  average: PlantSimNestedResult;
  sum: PlantSimNestedResult;
  standard_deviation: PlantSimNestedResult;
}

/** UI scope for the scoped `/timeseries?view=charts` endpoint. */
export type PlantSimResultScope =
  | { kind: 'plant' }
  | { kind: 'asset'; mv_bus: string; asset: string }
  | { kind: 'poi' }
  | { kind: 'market' }
  | { kind: 'shape' };

/**
 * Combined monthly + 12x24 payload scoped to a single UI area. The metric tree
 * has scope prefixes stripped (except `market`, which keeps its domain
 * prefixes) — identity is carried in `scope`, not in the metric paths.
 */
export interface PlantSimChartsResponse {
  view: 'charts';
  year: number;
  scope: PlantSimResultScope;
  units: PlantSimNestedResult;
  monthly: {
    months: Array<{
      month: number;
      timestamp: string;
      sum: PlantSimNestedResult;
      average: PlantSimNestedResult;
    }>;
  };
  twelve_by_twenty_four: {
    average: PlantSimNestedResult;
    sum: PlantSimNestedResult;
    standard_deviation: PlantSimNestedResult;
  };
}

interface PlantSimTimeseriesOptions {
  month?: number;
  granularity?: 'hourly';
}

export const plantApiV2 = api.extend({
  prefixUrl: '/api/v2',
});

export const runPlantSimSimulation = async (data: PlantSimSingleRunParameters): Promise<Task> =>
  plantApiV2.post('plant/simulate', { json: data }).json();

export const runPlantSimNetBenefitAnalysis = async (
  request: NetBenefitAnalysisV2Request,
): Promise<Task> => plantApiV2.post('plant/net-benefit', { json: request }).json();

export const parseNetBenefitResultsV2 = async (
  reportName: string | undefined,
  statistic?: string,
): Promise<Partial<NetBenefitAnalysisV2Columns>> =>
  plantApiV2
    .get('plant/net-benefit/parse', {
      searchParams: { reportName: reportName ?? '', statistic: statistic ?? '' },
    })
    .json();

export const getPlantSimulationLogs = async (): Promise<FlowRunLogs[]> =>
  api.get('plant/logs').json();

export const getPlantSimV2Results = async (
  serviceName: CoreService,
  opts?: ServiceListResultsSearchParams,
): Promise<ListResultsQuery<PlantSimDatabaseResultsEntry>> =>
  plantApiV2
    .get('plant/results', { searchParams: { serviceName, ...getServiceSearchParams(opts) } })
    .json();

const plantSimResultsPath = (id: string | undefined) => `proxy/plantsim-v2/results/${id}`;

export const usePlantSimInputsOutputsQuery = (id: string | undefined, year: number | 'all') =>
  useQuery({
    queryKey: ['plant-sim-inputs-outputs', id, year],
    queryFn: () =>
      api
        .get(`${plantSimResultsPath(id)}/inputs-outputs`, { searchParams: { year } })
        .json<PlantSimInputsOutputsResponse>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });

export const usePlantSimTimeseriesQuery = (
  id: string | undefined,
  year: number,
  opts: PlantSimTimeseriesOptions = {},
) =>
  useQuery({
    queryKey: ['plant-sim-timeseries', id, year, opts],
    queryFn: () =>
      api
        .get(`${plantSimResultsPath(id)}/timeseries`, { searchParams: { year, ...opts } })
        .json<PlantSimTimeseriesResponse>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const usePlantSimMonthlyQuery = (id: string | undefined, year: number) =>
  useQuery({
    queryKey: ['plant-sim-monthly', id, year],
    queryFn: () =>
      api
        .get(`${plantSimResultsPath(id)}/monthly`, { searchParams: { year } })
        .json<PlantSimMonthlyResponse>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
    // Keep the prior year's data on screen while the new year loads so the
    // charts don't flash to a loading state on every year switch.
    placeholderData: keepPreviousData,
  });

export const usePlantSimTwelveByTwentyFourQuery = (
  id: string | undefined,
  year: number,
  enabled = true,
) =>
  useQuery({
    queryKey: ['plant-sim-12x24', id, year],
    queryFn: () =>
      api
        .get(`${plantSimResultsPath(id)}/12x24`, { searchParams: { year } })
        .json<PlantSimTwelveByTwentyFourResponse>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id) && enabled,
    placeholderData: keepPreviousData,
  });

// Search params for the scoped charts endpoint. `asset` carries bus + asset;
// every other scope is just its kind.
const chartsScopeParams = (scope: PlantSimResultScope): Record<string, string> =>
  scope.kind === 'asset'
    ? { scope: 'asset', mv_bus: scope.mv_bus, asset: scope.asset }
    : { scope: scope.kind };

// Scope identity slots for the query key — absent bus/asset stay `undefined`,
// never empty strings.
const chartsScopeKey = (scope: PlantSimResultScope): Array<string | undefined> => [
  scope.kind,
  scope.kind === 'asset' ? scope.mv_bus : undefined,
  scope.kind === 'asset' ? scope.asset : undefined,
];

// True when a prior query's key targets the same scope (ignoring year), so we
// only carry placeholder data across a pure year switch — never across a scope
// change, which would flash another asset's charts under the new title.
const sameChartsScope = (previousKey: unknown, scope: PlantSimResultScope): boolean => {
  if (!Array.isArray(previousKey)) return false;
  const [kind, mvBus, asset] = previousKey.slice(3);
  const [nextKind, nextMvBus, nextAsset] = chartsScopeKey(scope);
  return kind === nextKind && mvBus === nextMvBus && asset === nextAsset;
};

const chartsQueryOptions = (id: string | undefined, year: number, scope: PlantSimResultScope) => ({
  queryKey: ['plant-sim-timeseries-charts', id, year, ...chartsScopeKey(scope)],
  queryFn: ({ signal }: QueryFunctionContext) =>
    api
      .get(`${plantSimResultsPath(id)}/timeseries`, {
        signal,
        searchParams: { year, view: 'charts', ...chartsScopeParams(scope) },
      })
      .json<PlantSimChartsResponse>(),
  staleTime: Number.POSITIVE_INFINITY,
});

export const usePlantSimTimeseriesChartsQuery = (
  id: string | undefined,
  year: number,
  scope: PlantSimResultScope,
  enabled = true,
  // Kept for call-site compatibility. Next-year warming is no longer piggybacked
  // on this query (it raced the selected year); use `usePlantSimChartsPrefetch`
  // wired to the year slider's prev/next hover instead.
  _maxYear?: number,
) =>
  useQuery({
    ...chartsQueryOptions(id, year, scope),
    enabled: Boolean(id) && enabled,
    placeholderData: (previous, previousQuery) =>
      sameChartsScope(previousQuery?.queryKey, scope) ? previous : undefined,
  });

/**
 * Returns a callback that warms a neighbouring year's charts for a given scope.
 * Wire it to the year slider's prev/next button hover so stepping feels instant
 * without eagerly fetching (and racing) a year the user never selects. Bounds
 * are enforced: years below 1 or above `maxYear` are skipped so an out-of-range
 * request never 404s.
 */
export const usePlantSimChartsPrefetch = (
  id: string | undefined,
  scope: PlantSimResultScope,
  maxYear?: number,
) => {
  const queryClient = useQueryClient();
  const [scopeKind, scopeMvBus, scopeAsset] = chartsScopeKey(scope);
  // `scope` is reconstructed from its key parts so the callback identity only
  // changes when the scope actually changes, not on every fresh object.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scope via key parts
  return useCallback(
    (year: number) => {
      if (!id || year < 1 || (maxYear !== undefined && year > maxYear)) return;
      // A year switch fires three queries — warm all of them, not just charts.
      void queryClient.prefetchQuery(chartsQueryOptions(id, year, scope));
      void queryClient.prefetchQuery({
        queryKey: ['plant-sim-inputs-outputs', id, year],
        queryFn: () =>
          api
            .get(`${plantSimResultsPath(id)}/inputs-outputs`, { searchParams: { year } })
            .json<PlantSimInputsOutputsResponse>(),
        staleTime: Number.POSITIVE_INFINITY,
      });
      void queryClient.prefetchQuery({
        queryKey: ['plant-sim-timeseries', id, year, { granularity: 'hourly' }],
        queryFn: () =>
          api
            .get(`${plantSimResultsPath(id)}/timeseries`, {
              searchParams: { year, granularity: 'hourly' },
            })
            .json<PlantSimTimeseriesResponse>(),
        staleTime: Number.POSITIVE_INFINITY,
      });
    },
    [queryClient, id, maxYear, scopeKind, scopeMvBus, scopeAsset],
  );
};

export const getPlantSimConfigurations = async <T = PlantSimV2Configuration>(
  queryOptions?: QueryConfigsSearchParams,
): Promise<ConfigQueryResults<T>> => {
  const searchParams = getQueryConfigsSearchParams(queryOptions);
  return plantApiV2.get('plant/configs', { searchParams }).json();
};

export const getPlantSimConfiguration = async (
  id: string | undefined | null,
  version: number | undefined,
): Promise<PlantSimV2Configuration> =>
  plantApiV2
    .get(`plant/configs/${id}`, version !== undefined ? { searchParams: { version } } : {})
    .json<PlantSimV2Configuration>();

export const savePlantSimConfiguration = async (
  request: PlantSimV2RequestWithName,
): Promise<PlantSimV2Configuration> => plantApiV2.post('plant/configs', { json: request }).json();

export const editPlantSimConfiguration = async (
  request: PlantSimV2RequestWithName,
  id: string | undefined | null,
  version: number,
): Promise<PlantSimV2Configuration> =>
  plantApiV2.post(`plant/configs/${id}/${version}`, { json: request }).json();

export const getPlantSimMaxVersion = async (id: string | undefined | null): Promise<number> =>
  plantApiV2.get(`plant/configs/${id}/max-version`).json();

export const usePlantSimResultsQuery = (id: string | undefined | null) =>
  useQuery<ScenarioRecord | null>({
    queryKey: ['plant-sim-results-record', id],
    queryFn: () =>
      plantApiV2
        .get(`plant/results/${id}`, {
          searchParams: {
            plant_sim: true,
            pv_sim: true,
            ess_costing: true,
            workflow: true,
            project: true,
            id_key: 'scenario_id',
            exclude_ts: false,
          },
        })
        .json<ScenarioRecord | null>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const usePlantSimScenarioQuery = (scenarioId: string | undefined | null) =>
  useQuery({
    queryKey: ['plantsim-scenario-query', scenarioId],
    queryFn: () =>
      plantApiV2
        .get(`plant/results/${scenarioId}`, {
          searchParams: {
            'plant_sim.request': true,
            workflow: true,
            id_key: 'scenario_id',
          },
        })
        .json<{ request: PlantSimRequest; workflow: ScenarioWorkflow }>(),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(scenarioId),
  });

export const usePlantsimConfigQuery = (id: string | null | undefined, version?: number) =>
  useQuery<PlantSimV2Configuration>({
    queryKey: ['plantsim-config' satisfies `${ConfigType}-config`, id, version],
    queryFn: () => getPlantSimConfiguration(id ?? null, version),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const useCurvesQuery = () =>
  useQuery<PriceLookups>({
    queryKey: ['price-lookups', ...priceTypesForApi],
    queryFn: () => getPriceLookups(priceTypesForApi),
  });
